import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Container, Paper, Typography } from '@mui/material';
import { Agreement, agreementService } from '../services/agreementService';
import { useAuth } from '../contexts/AuthContext';
import { AgreementSign } from './AgreementModal';
import { DocusealAgreementSign } from './DocusealAgreementSign';

interface AgreementGateProps {
  children: React.ReactNode;
}

export const AgreementGate: React.FC<AgreementGateProps> = ({ children }) => {
  const { firmAccess } = useAuth();
  const downloadOnly = firmAccess?.mode === 'download_only';
  const [loading, setLoading] = useState(true);
  const [pendingAgreements, setPendingAgreements] = useState<Agreement[]>([]);
  const [currentAgreementIndex, setCurrentAgreementIndex] = useState(0);
  // Agreement ids whose DocuSeal session could not start, so they render the
  // built-in HTML signing flow instead.
  const [htmlFallbackIds, setHtmlFallbackIds] = useState<number[]>([]);
  const [fallbackNotice, setFallbackNotice] = useState<string | null>(null);

  const loadPendingAgreements = async () => {
    try {
      setLoading(true);
      const agreements = await agreementService.getPendingAgreements();
      setPendingAgreements(agreements);
      setCurrentAgreementIndex(0);
    } catch (_err) {
      // On error, don't block the app UI
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (downloadOnly) {
      setLoading(false);
      return;
    }
    loadPendingAgreements();
  }, [downloadOnly]);

  const handleAgreementSigned = () => {
    setFallbackNotice(null);
    if (currentAgreementIndex < pendingAgreements.length - 1) {
      setCurrentAgreementIndex(currentAgreementIndex + 1);
    } else {
      loadPendingAgreements();
    }
  };

  const handleDocusealFallback = useCallback((agreementId: number, reason?: string) => {
    setHtmlFallbackIds((prev) => (prev.includes(agreementId) ? prev : [...prev, agreementId]));
    setFallbackNotice(reason || null);
  }, []);

  if (downloadOnly) {
    return <>{children}</>;
  }

  if (!loading && pendingAgreements.length > 0) {
    const currentAgreement = pendingAgreements[currentAgreementIndex];
    const useDocuseal =
      currentAgreement?.signing_method === 'docuseal' &&
      !htmlFallbackIds.includes(currentAgreement.id);

    if (useDocuseal) {
      return (
        <Box
          sx={{
            height: '100vh',
            maxHeight: '100vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#f5f5f5',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.5,
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} color="primary">
              Agreement required
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {currentAgreementIndex + 1} of {pendingAgreements.length}: {currentAgreement?.title}
            </Typography>
          </Box>

          {fallbackNotice && (
            <Alert severity="info" sx={{ mx: 2, mt: 2 }} onClose={() => setFallbackNotice(null)}>
              {fallbackNotice}
            </Alert>
          )}

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <DocusealAgreementSign
              key={currentAgreement.id}
              agreement={currentAgreement}
              onSigned={handleAgreementSigned}
              onFallback={(reason) => handleDocusealFallback(currentAgreement.id, reason)}
            />
          </Box>
        </Box>
      );
    }

    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper elevation={3} sx={{ p: 3, mb: 3, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom color="primary">
            Agreement Required
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Before you can proceed, you must review and sign the required agreement(s).
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Agreement {currentAgreementIndex + 1} of {pendingAgreements.length}:{' '}
            <strong>{currentAgreement?.title}</strong>
          </Typography>
        </Paper>

        {fallbackNotice && (
          <Alert severity="info" sx={{ mb: 3 }} onClose={() => setFallbackNotice(null)}>
            {fallbackNotice}
          </Alert>
        )}

        <AgreementSign agreement={currentAgreement} onSigned={handleAgreementSigned} />
      </Container>
    );
  }

  // Render app normally while checking or when nothing pending
  return <>{children}</>;
};
